/**
 * Machine-local map of kitchen station -> physical printer assignment
 * (device name + paper width).
 *
 * Printers are physical to a given terminal: "EPSON TM-T20" exists on THIS PC
 * and not on the next counter's PC, and a station's printer may be 58mm while
 * another is 80mm. So the assignment belongs on the machine, not in the shared
 * tenant database. We persist it in localStorage, which in the desktop app is
 * per-origin (the local server) and is wiped by the Electron shell when the
 * machine is re-pointed to another tenant, so it never bleeds across
 * restaurants.
 *
 * Resolved client-side at print time (see printKotReceipts), so it works
 * regardless of whether the backend knows about per-station printers — the
 * backend already returns one slip per station with its station_id.
 */

const KEY = 'bhookly-station-printers'

export interface StationPrinterConfig {
  /** OS printer name; '' => OS default printer. */
  device: string
  /** Paper width in mm for this station's printer (e.g. 58, 80). Undefined => use the global default. */
  paperWidthMm?: number
}

// Stored value is either a bare device string (legacy) or the config object.
type StoredValue = string | StationPrinterConfig

function readMap(): Record<string, StoredValue> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(KEY)
    const parsed = raw ? JSON.parse(raw) : {}
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, StoredValue>) : {}
  } catch {
    return {}
  }
}

function writeMap(map: Record<string, StoredValue>): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(KEY, JSON.stringify(map))
  } catch {
    // localStorage unavailable/full — non-fatal; the slip falls back to the OS
    // default printer.
  }
}

/** The full printer config for a station on this machine. */
export function getStationPrinterConfig(stationId: string): StationPrinterConfig {
  if (!stationId) return { device: '' }
  const v = readMap()[stationId]
  if (!v) return { device: '' }
  if (typeof v === 'string') return { device: v } // legacy bare-string entries
  return { device: v.device || '', paperWidthMm: v.paperWidthMm }
}

/** Convenience: just the device name for a station ('' if none). */
export function getStationPrinter(stationId: string): string {
  return getStationPrinterConfig(stationId).device
}

/**
 * Assign (or clear) a station's printer on this machine. An empty device clears
 * the whole entry; a positive paperWidthMm is stored, otherwise it's omitted
 * (falls back to the global default width at print time).
 */
export function setStationPrinterConfig(stationId: string, cfg: StationPrinterConfig): void {
  if (!stationId) return
  const map = readMap()
  const device = (cfg.device || '').trim()
  if (!device && !cfg.paperWidthMm) {
    delete map[stationId]
  } else {
    const entry: StationPrinterConfig = { device }
    if (cfg.paperWidthMm && cfg.paperWidthMm > 0) entry.paperWidthMm = cfg.paperWidthMm
    map[stationId] = entry
  }
  writeMap(map)
}

// ── Counter / receipt printer ──────────────────────────────────────────────
// Customer receipts, Z-reports, credit notes and PRA invoices print to the
// "counter" printer. By default that's the OS default printer; this lets an
// operator pick a specific device on this machine instead. Stored separately
// from the per-station map. Width follows the global receipt settings.
const RECEIPT_KEY = 'bhookly-receipt-printer'

export function getReceiptPrinter(): string {
  if (typeof window === 'undefined') return ''
  try {
    return window.localStorage.getItem(RECEIPT_KEY) || ''
  } catch {
    return ''
  }
}

export function setReceiptPrinter(device: string): void {
  if (typeof window === 'undefined') return
  try {
    const d = (device || '').trim()
    if (d) window.localStorage.setItem(RECEIPT_KEY, d)
    else window.localStorage.removeItem(RECEIPT_KEY)
  } catch {
    // non-fatal — falls back to the OS default printer
  }
}

// ── Print offset (F-FE-N113) ───────────────────────────────────────────────
// How many character cells to inset every ESC/POS slip from the left.
//
// Machine-local for the same reason as everything above: it is a statement
// about how the ROLL SITS IN THIS PRINTER, not about the tenant. F-FE-N110
// stopped slips being clipped by leaving a character of margin each side, but
// Mizaj's still print left of centre with a gap on the right — and from a
// photograph there is no way to tell whether the printer ignores our GS L or
// the roll is simply seated off centre. The second cannot be corrected by a
// symmetric inset at all; it needs an asymmetric one, and only the person
// holding the paper can say how much.
//
// Applied as literal spaces rather than a larger GS L, because a space is a
// character no firmware can decline to print.
//
// F-FE-N156: the desktop reads the SAME number and applies it as CSS at the
// print seam (printTransport.ts via printOffset.ts, 1.5 mm per column), so one
// value nudges every slip this device prints, whichever shell it is.
const OFFSET_KEY = 'bhookly-print-offset-cols'
/** Wider than this and a receipt would lose more than it gains. */
const MAX_OFFSET_COLS = 8

export function getPrintOffsetCols(): number {
  if (typeof window === 'undefined') return 0
  try {
    const n = parseInt(window.localStorage.getItem(OFFSET_KEY) ?? '', 10)
    if (!Number.isFinite(n) || n <= 0) return 0
    return Math.min(n, MAX_OFFSET_COLS)
  } catch {
    return 0
  }
}

export function setPrintOffsetCols(cols: number): void {
  if (typeof window === 'undefined') return
  try {
    const n = Math.max(0, Math.min(Math.round(cols) || 0, MAX_OFFSET_COLS))
    if (n > 0) window.localStorage.setItem(OFFSET_KEY, String(n))
    else window.localStorage.removeItem(OFFSET_KEY)
  } catch {
    // non-fatal — the slip prints at the default inset
  }
}

// ── Counter print agent (F-FE-N60) ─────────────────────────────────────────
// Whether THIS machine prints the KOTs that tablets relay through the backend.
// Machine-local for the same reason the maps above are: it is a statement about
// this PC's hardware, not about the tenant.
//
// Default OFF, deliberately. Two counter PCs both claiming would split a
// service's tickets between two printers, and the operator would see half the
// paper with no error anywhere — far worse than nothing printing at all, which
// is at least obvious.
const AGENT_KEY = 'bhookly-print-agent'

export interface PrintAgentConfig {
  enabled: boolean
  /**
   * Free-text label recorded on each claimed job ("Counter 1"). Diagnostics
   * only — it is not a routing key, and there is no device registry behind it.
   */
  terminalName: string
  /** Stable per-machine id, minted on first enable. */
  terminalId: string
}

const AGENT_DEFAULT: PrintAgentConfig = { enabled: false, terminalName: '', terminalId: '' }

export function getPrintAgentConfig(): PrintAgentConfig {
  if (typeof window === 'undefined') return { ...AGENT_DEFAULT }
  try {
    const raw = window.localStorage.getItem(AGENT_KEY)
    if (!raw) return { ...AGENT_DEFAULT }
    const v = JSON.parse(raw) as Partial<PrintAgentConfig>
    return {
      enabled: v.enabled === true,
      terminalName: typeof v.terminalName === 'string' ? v.terminalName : '',
      terminalId: typeof v.terminalId === 'string' ? v.terminalId : '',
    }
  } catch {
    return { ...AGENT_DEFAULT }
  }
}

/**
 * Fired whenever the agent config changes, so a running print agent can start
 * or stop on the spot.
 *
 * Without this the agent read the flag once at mount: an operator could switch
 * "print tablet KOTs on this PC" ON, watch the tile say active, and still have
 * nothing claimed until the app was restarted — with the tablets queueing paper
 * that never came out. Silent, and indistinguishable from a broken relay.
 */
const AGENT_EVENT = 'bhookly:print-agent-changed'

export function subscribePrintAgentConfig(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(AGENT_EVENT, onChange)
  return () => window.removeEventListener(AGENT_EVENT, onChange)
}

export function setPrintAgentConfig(cfg: Partial<PrintAgentConfig>): PrintAgentConfig {
  const current = getPrintAgentConfig()
  const next: PrintAgentConfig = {
    enabled: cfg.enabled ?? current.enabled,
    terminalName: (cfg.terminalName ?? current.terminalName).trim().slice(0, 40),
    // Minted once and then kept, so the audit list can tell two terminals apart
    // even if an operator renames one of them mid-service.
    terminalId: current.terminalId || cfg.terminalId || newTerminalId(),
  }
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(AGENT_KEY, JSON.stringify(next))
    } catch {
      // non-fatal — the agent simply won't persist across a reload
    }
    window.dispatchEvent(new CustomEvent(AGENT_EVENT))
  }
  return next
}

/**
 * The stable id for THIS machine, minting one on first call (F-CTRL-N52).
 *
 * `terminalId` was previously minted only inside setPrintAgentConfig, i.e. only
 * on a terminal where somebody had switched the print agent on. Offline ticket
 * numbering needs a per-machine identity on every terminal — two tills dark at
 * the same time must not both print "OFF-1" — so it cannot depend on an
 * unrelated feature having been enabled.
 *
 * Deliberately reuses the same key rather than minting a second device id: two
 * competing notions of "which machine is this" is exactly the kind of drift the
 * print-agent audit list exists to resolve.
 */
let memoTerminalId = ''

export function getOrCreateTerminalId(): string {
  const current = getPrintAgentConfig()
  if (current.terminalId) return current.terminalId
  // Memoised so the id is stable for the life of the process even when it
  // cannot be persisted — private mode, a full quota, or a non-DOM context.
  // Without this, a terminal whose localStorage write fails mints a fresh id on
  // every call, so consecutive tickets in one outage would each claim to come
  // from a different till.
  if (memoTerminalId) return memoTerminalId

  memoTerminalId = newTerminalId()
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(
        AGENT_KEY,
        JSON.stringify({ ...current, terminalId: memoTerminalId }),
      )
    } catch {
      // Non-fatal — the memo above still holds it for this session. A repeated
      // ticket string across launches is cosmetic; refusing to sell is not.
    }
  }
  return memoTerminalId
}

/** Test seam — forces the next call to mint again. */
export function __resetTerminalIdForTests(): void {
  memoTerminalId = ''
}

function newTerminalId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    // fall through
  }
  return `t-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * The label sent with a claim. Name plus a short id suffix, because two PCs are
 * often both called "Counter" and the point of the field is telling them apart.
 * Capped at 64 chars to match the print_jobs.claimed_by column.
 */
export function printAgentClaimLabel(cfg: PrintAgentConfig): string {
  const name = cfg.terminalName.trim()
  const short = cfg.terminalId.slice(0, 8)
  const label = name ? `${name} (${short})` : short
  return label.slice(0, 64)
}
